import { BrowserCacheLocation, EventType, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider, useAccount, useMsal } from '@azure/msal-react';
import { CogniteClient } from '@cognite/sdk';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { env } from '#core/lib/utils/env.ts';

import { CogniteClientContext } from './CogniteClientContext.ts';

// TODO: move to schema/env.ts
const { cluster, clientId, project, tenantId } = env.auth ?? {};
const baseUrl = `https://${cluster}.cognitedata.com`;
const scopes: string[] = [`${baseUrl}/.default`];
const request = { scopes };

export function MsalAppAuthProvider({ children }: React.PropsWithChildren) {
  if (!env.auth) {
    return children;
  }

  const { cluster, clientId, tenantId, redirectUri } = env.auth;
  const baseUrl = `https://${cluster}.cognitedata.com`;
  const scopes: string[] = [`${baseUrl}/.default`];
  const request = { scopes };

  const pca = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
  });

  pca.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event?.payload &&
      'account' in event.payload &&
      event.payload?.account
    ) {
      const account = event.payload?.account;
      console.debug('Login successful, setting active account:', account);
      pca.setActiveAccount(account);
    }
  });

  return (
    <MsalProvider instance={pca}>
      <MsalAuthenticationTemplate authenticationRequest={request} interactionType={InteractionType.Redirect}>
        <CogniteClientProvider>{children}</CogniteClientProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

export function CogniteClientProvider({ children }: React.PropsWithChildren) {
  const { accounts, instance } = useMsal();
  const account = useAccount(accounts.find((account) => account.tenantId === tenantId) ?? {});
  useEffect(() => {
    async function checkActiveAccount() {
      if (!account) {
        instance.setActiveAccount(null);
        await instance.acquireTokenRedirect(request);
      }
    }
    void checkActiveAccount();
  }, [instance, account]);

  const { data: authenticatedClient } = useQuery({
    queryKey: ['cogniteAuth', account] as const,
    queryFn: async ({ queryKey: [, account] }) => {
      if (!account) {
        return null;
      }

      const client = new CogniteClient({
        appId: clientId ?? '',
        baseUrl: env.cdf?.url ?? baseUrl,
        project: project ?? '',
        async oidcTokenProvider() {
          const { accessToken } = await instance.acquireTokenSilent({
            ...request,
            account,
          });

          return accessToken;
        },
      });
      await client.authenticate();
      return client;
    },
  });

  if (!authenticatedClient) {
    return <LabeledLoader label="Authenticating..." />;
  }

  return <CogniteClientContext value={authenticatedClient}>{children}</CogniteClientContext>;
}
